import { useSignupPage, signupErrorReason } from 'utils/signupAnalytics';
import React, {useEffect, useState, useRef} from 'react'
import * as ls from "utils/localStorage";
import {useHistory, useLocation} from 'react-router';
import Api from "../../../api";
import config from '../../../../src/api/config'
import {Button, FormControlLabel, Checkbox, InputAdornment, IconButton, CircularProgress} from '@material-ui/core'
import MaterialInput from "../../../components/Inputs/MaterialInput";
import {exitReact, login} from "../../../utils";
import {Visibility, VisibilityOff, CheckCircle, Cancel} from "@material-ui/icons";

import {
    Page,
    Container
} from "./SignUp0.styles";
import SignUpHeader from "../../../components/SignUpHeader";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import {roles} from "utils/constants";
import {usePersistedState} from "../../../hooks";
import entities from "../../../reduxStore/entityKeys";
import {mapReferralValidationResult} from "./referralValidation";
import { isWhitespaceOnlyPassword, SIGNUP_WHITESPACE_PASSWORD_ERROR } from '../../../utils/signupText';

// @ts-ignore
const TempStars = window.TempStars;
// @ts-ignore
const app = window.app;
// @ts-ignore
const dataLayer = window?.dataLayer


interface ErrorProps {
    email?: string;
    password?: string;
    refCode?: string;
    terms?: string;
    err?: string;
}

export const getSignUp0RoleConfig = (selectedRole: 'office' | 'worker') => ({
    roleId: selectedRole === 'office' ? roles.DENTIST : roles.HYGIENIST,
    pathname: selectedRole === 'office' ? 'SignUp1_DO' : 'SignUp1_Talent',
});

export const validateSignUp0Fields = ({
    email,
    password,
    confirmedPolicy,
    inviteCodeValue,
    refCodeStatus,
}: {
    email: string;
    password: string;
    confirmedPolicy: boolean;
    inviteCodeValue: string;
    refCodeStatus: string | null;
}): ErrorProps => {
    let errors: ErrorProps = {};
    if (!email.length) {
        errors = {...errors, email: 'Email cannot be empty'};
    }
    if (password.length < 6) {
        errors = {...errors, password: 'Your password must be at least 6 characters'};
    }
    if (isWhitespaceOnlyPassword(password)) {
        errors = {...errors, password: SIGNUP_WHITESPACE_PASSWORD_ERROR};
    }
    if (!confirmedPolicy) {
        errors = {...errors, terms: 'You must confirm to continue'};
    }
    if (inviteCodeValue.length && refCodeStatus === 'invalid') {
        errors = {...errors, refCode: 'Invalid referral code. Please check for typos or leave blank to skip.'};
    }
    return errors;
};

export default function SignUp0() {
    const history = useHistory();
    const { state } = useLocation<{selectedRole: 'office' | 'worker', refCode: string | ''}>();
    const { selectedRole, refCode } = state;
    const signup = useSignupPage('create_account', selectedRole === 'office' ? 'office' : 'talent');
    const [errorMessage, setErrorMessage] = useState<ErrorProps>({});
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const analyticsTracked = useRef(false);

    // T2-5090: live referral-code validation as the user types (debounced 500ms).
    const [refCodeValue, setRefCodeValue] = useState<string>(refCode || '');
    const [refCodeStatus, setRefCodeStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
    const [referrerName, setReferrerName] = useState<string>('');
    // Tracks the latest typed code so an out-of-order validation response can be discarded. (PR #1721 review)
    const latestRefCodeRef = useRef<string>((refCode || '').trim());

    useEffect(() => {
        const code = (refCodeValue || '').trim();
        latestRefCodeRef.current = code;
        if (code.length < 3) {
            setRefCodeStatus('idle');
            setReferrerName('');
            return;
        }
        setRefCodeStatus('checking');
        const handle = setTimeout(async () => {
            try {
                const res = await Api.validateReferralCode(code);
                // Ignore a response that no longer matches what's typed (out-of-order responses
                // from rapid edits must not clobber a newer result). (PR #1721 review)
                if (latestRefCodeRef.current !== code) {
                    return;
                }
                // Partner / FREE_PLACEMENT campaign codes come back valid with no referrerName —
                // they must unblock signup just like a user-to-user code. (PR #2161 review)
                const { status, referrerName: name } = mapReferralValidationResult(res);
                setRefCodeStatus(status);
                setReferrerName(name);
            } catch {
                // On a validation request failure, don't block signup — fall back to idle so the
                // backend remains the source of truth at submit time.
                setRefCodeStatus('idle');
            }
        }, 500);
        return () => clearTimeout(handle);
    }, [refCodeValue]);

    const setUser = usePersistedState({
        entityName: entities.user,
    })[1];

    useEffect(() => {
        if (!analyticsTracked.current) {
            const eventName = selectedRole === 'office' 
                ? 'view_office_createAccount' 
                : 'view_talent_createAccount';
            TempStars.Analytics.track(eventName, {
                role: selectedRole,
                hasReferralCode: !!refCode
            });
            analyticsTracked.current = true;
        }
    }, [selectedRole, refCode]);

    const handleClickShowPassword = () => {
        setShowPassword((show) => !show);
    }

    const onSubmit = async (event: any) => {
        event.preventDefault();
        const {email, password, confirmedPolicy} = event.target
        const inviteCodeValue = (refCodeValue || '').trim();
        let errors = validateSignUp0Fields({
            email: email.value,
            password: password.value,
            confirmedPolicy: confirmedPolicy.checked,
            inviteCodeValue,
            refCodeStatus,
        });
        if (Object.keys(errors).length) {
            signup.error('validation_failed');
            return setErrorMessage(errors);
        }

        try {
            TempStars.Analytics.track('sign-up - submission started', { role: selectedRole, signup_flow: 'referral' });
            const userByEmail = await TempStars.Api.findUserByEmail(email.value)
            if (userByEmail.isFound) {
                signup.error('duplicate_account'); signup.duplicate();
                errors = {...errors, email: 'User with this email already exists'};
                return setErrorMessage(errors)
            }
            app.showPreloader('Creating Account');
            const {userId} = await Api.createUser({
                email: email.value,
                password: password.value,
                affiliateCode: inviteCodeValue,
            })
            
            // T2-4512: Identity stitching - alias then identify BEFORE login
            TempStars.Analytics.alias(String(userId));
            TempStars.Analytics.identify(String(userId));
            
            await login(email.value, password.value, setUser)
            
            // T2-4512: Set UTM last-touch properties AFTER identify
            TempStars.User.setAnalytics();
            
            await Api.updateToken()
            
            // T2-4512: Track create account event AFTER identity is established (no userType)
            TempStars.Analytics.track('sign-up - create account');
            
            // T2-4512: Set profile properties AFTER identify
            if (inviteCodeValue) {
                TempStars.Analytics.setProfileProperties({
                    affiliateCode: inviteCodeValue,
                });
            }

            dataLayer?.push({
                'event' : 'sign_up',
                'mail' : email.value,
            });

            const { roleId, pathname } = getSignUp0RoleConfig(selectedRole);
            await Api.createUserRole({roleId, userId: Number(userId)});
            signup.submitted();
            app.hidePreloader();
            return history.push({
                pathname,
            }, {
                userId,
            })

        } catch (e: any) {
            signup.error(signupErrorReason(e));
            if (signupErrorReason(e) === 'duplicate_account') signup.duplicate();
            TempStars.Analytics.track('sign-up - error')
            app.hidePreloader();
            const raw = (e && (e.message || e.body || e.toString())) || '';
            let friendly = '';
            try {
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                if (parsed && typeof parsed === 'object') {
                    friendly = parsed.error?.message || parsed.message || '';
                    const code = parsed.error?.code;
                    if (code === 'INVALID_REFERRAL_CODE') {
                        return setErrorMessage({refCode: 'Invalid referral code'});
                    }
                    if (code === 'EXPIRED_REFERRAL_CODE') {
                        return setErrorMessage({refCode: 'That code is no longer valid'});
                    }
                    if (code === 'USER_ALREADY_EXISTS') {
                        return setErrorMessage({email: 'User with this email already exists'});
                    }
                }
            } catch {}
            const fallback = typeof raw === 'string' ? raw : String(raw);
            return setErrorMessage({err: friendly || fallback});
        }
    }

    const checkboxLabel = <div className="checkboxLabel">
        I confirm that I have read and agree to TempStars <a
        className={"external underlinedLink"} href={`https://${config.web.CA.siteUrl}/terms`}
        target="_system">Terms of Service</a> and <a
        className={"external underlinedLink"} href={`https://${config.web.CA.siteUrl}/privacy`}
        target="_system">Privacy Policy</a>.
    </div>

    // Retained legacy referral form. Current public referral links open Landing.
    // There is no in-app navigation into this form, so a "Back" button has
    // nowhere sensible to go — and the
    // old target (RoleSelect) expects an already-created userId and throws
    // "Something went wrong" when reached pre-signup. So we render no Back button.

    const logIn = () => {
        try {
            history.push({
                pathname: 'Login'
            })
        } catch (e: any) {
            console.log('CANNOT BACK?', e)
        }
    }

    return (
        <Page>
            <Container data-page="signup" className={'no-swipeback'}>
                {/* T2-5090: no Back button — SignUp0 is a referral deep-link landing (see note above). */}
                <HeaderStatusBar />

                <div className={"bodySignUp"}>

                    <SignUpHeader
                        title={selectedRole === 'office'
                            ? 'Dental Office'
                            : 'Dental Talent'
                        }
                        description={'Sign Up: Step 1 of 3'}
                        error={errorMessage?.err
                            || (errorMessage
                                && (errorMessage?.email || errorMessage?.password || errorMessage?.refCode || errorMessage?.terms)
                                && 'Please fix the errors below')
                        }
                    />

                    <div className="content-block contentBlock">
                        <form id="signup-form" data-testid="signup-referral-form" onSubmit={onSubmit}>
                            <div>
                                <MaterialInput
                                    className={'inset'}
                                    label="Email"
                                    type="email"
                                    id={'email'}
                                    name={"email"}
                                    error={!!errorMessage?.email}
                                    helperText={!!errorMessage?.email && errorMessage.email}
                                />
                                <MaterialInput
                                    className={'inset'}
                                    label="Password (at least 6 characters)"
                                    id={'password'}
                                    name={"password"}
                                    type={showPassword ? 'text' : 'password'}
                                    error={!!errorMessage?.password}
                                    helperText={!!errorMessage?.password && errorMessage.password}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                className={'no-fastclick'}
                                                aria-label="visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <MaterialInput
                                    className={'inset'}
                                    label="Referral Code (Optional)"
                                    id={'inviteCode'}
                                    name={"inviteCode"}
                                    type={"text"}
                                    value={refCodeValue}
                                    onChange={(e) => {
                                        setRefCodeValue(e.target.value);
                                        // Clear any stale submit-time referral error as the user edits. (PR #1721 review)
                                        if (errorMessage?.refCode) {
                                            setErrorMessage((prev) => ({ ...prev, refCode: undefined }));
                                        }
                                    }}
                                    error={refCodeStatus === 'invalid' || !!errorMessage?.refCode}
                                    helperText={
                                        refCodeStatus === 'valid'
                                            ? `Valid!${referrerName ? ` Referred by ${referrerName}` : ''}`
                                            : refCodeStatus === 'invalid'
                                            ? 'Invalid referral code. Please check for typos or leave blank to skip.'
                                            : errorMessage?.refCode
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {refCodeStatus === 'checking' && <CircularProgress size={18} />}
                                            {refCodeStatus === 'valid' && <CheckCircle style={{ color: '#13CA82' }} />}
                                            {refCodeStatus === 'invalid' && <Cancel style={{ color: '#FB7672' }} />}
                                        </InputAdornment>
                                    }
                                />
                                <div className={'inputTextField inset'}>
                                    <FormControlLabel
                                        className={'no-fastclick'}
                                        control={<Checkbox
                                        name={'confirmedPolicy'}
                                        icon={<img id="officeCheckmark" className="hidden-icon" alt="" src={"img/checkboxUnchecked.svg"} />}
                                        checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt="" src={"img/checkboxChecked.svg"} />}
                                    />} label={checkboxLabel} />
                                    {errorMessage && errorMessage.terms && <div id="agreed-error-msg" className={'errorMsg'}>{errorMessage.terms}</div>}
                                </div>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    id="signup-button"
                                    className="bigButtonInNewStyle"
                                    type={'submit'}
                                    disabled={!!refCodeValue.trim().length && refCodeStatus === 'invalid'}
                                >
                                    Create Account
                                </Button>
                            </div>
                        </form>

                        <div className={'toLogIn'}>
                            {`Already have an account? `}
                            <div className="underlinedLink" onClick={logIn}>Log In</div>
                        </div>

                        <div className="helpLinks">
                            <a href={`mailto:${config.CA.helpEmail}`} className="external footerMsg underlinedLink">Contact Support</a>
                        </div>
                    </div>
                </div>

                <div className="footer footerLinks"></div>
            </Container>
        </Page>
    )
}
